import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtModal from "../../components/NtModal";
import NtModalAccountPicker from "../../extensions/components/NtModalAccountPicker";
import NtModalConfirmAlert from "../../components/NtModalConfirmAlert";
import NtPageSection from "../../components/NtPageSection";
import NtExtensionTag from "../../components/NtExtensionTag";
import { useTheme } from "../../context/ThemeContext";
import { parseExtension } from "../../extensions/utils/ExtensionUtils";
import {
  createUserExtension,
  deleteUserExtension,
  fetchUsersExtensions,
} from "../../api/user";
import UserContext from "../../context/UserContext";
import Logger from "../../common/utils/Logger";

function NtUserExtensions({ containerStyle, userId }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const accountPickerRef = useRef();
  const confirmRef = useRef();
  const context = useContext(UserContext);
  const [data, setData] = useState([]);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const handleDataFetch = async () => {
    const response = await fetchUsersExtensions(
      context.user?.domain,
      userId,
      1,
      10000,
      "",
      {},
    );
    Logger("User extensions fetch", response);
    if (response.ok) setData(response.data?.data || []);
  };

  const handleCreate = async (extension) => {
    const response = await createUserExtension(
      context.user?.domain,
      userId,
      extension,
    );
    if (response.ok) handleDataFetch();
  };

  const handleDelete = async () => {
    if (!pendingDeleteId) return;
    const response = await deleteUserExtension(
      context.user?.domain,
      userId,
      pendingDeleteId,
    );
    if (response.ok) handleDataFetch();
    setPendingDeleteId(null);
  };

  useEffect(() => {
    if (userId) handleDataFetch();
  }, [userId]);

  const addBtn = (
    <NtTouchableEffect
      style={styles.addBtn}
      onPress={() => accountPickerRef.current?.show()}
    >
      <NtText style={styles.addText}>+ Add</NtText>
    </NtTouchableEffect>
  );

  return (
    <NtPageSection title="Extensions" trailing={addBtn} style={containerStyle}>
      <View style={styles.body}>
        {data.length === 0 ? (
          <NtText style={styles.empty}>No extensions assigned</NtText>
        ) : (
          data.map((item) => {
            const num = parseExtension(
              item.extension,
              context?.user?.domain,
            );
            const tollAllow =
              item.toll_allow && item.toll_allow == "t"
                ? "enabled"
                : "disabled";
            return (
              <View
                key={item.extension_uuid || item.extension}
                style={styles.row}
              >
                <View style={styles.ico}>
                  <NtText style={styles.icoText}>E</NtText>
                </View>
                <View style={styles.bodyCol}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <NtExtensionTag extension={num} size="sm" />
                    {item.effective_caller_id_name ? (
                      <NtText style={styles.label}>
                        {item.effective_caller_id_name}
                      </NtText>
                    ) : null}
                  </View>
                  {item.description ? (
                    <NtText style={styles.meta} numberOfLines={1}>
                      {item.description}
                    </NtText>
                  ) : null}
                </View>
                <NtText style={styles.meta}>Toll: {tollAllow}</NtText>
                <NtTouchableEffect
                  style={styles.del}
                  onPress={() => {
                    setPendingDeleteId(item.extension);
                    confirmRef.current?.show();
                  }}
                >
                  <NtMaterialIcon
                    name="close"
                    color={theme.textDim}
                    size={14}
                  />
                </NtTouchableEffect>
              </View>
            );
          })
        )}
      </View>

      <NtModal ref={accountPickerRef}>
        <NtModalAccountPicker
          removeOptions={["ringgroup", "recording", "voicemail", "other"]}
          defaultOpenTab={"extension"}
          onCancel={() => accountPickerRef.current?.dismiss()}
          onPress={(selection) => {
            accountPickerRef.current?.dismiss();
            handleCreate(selection?.item?.extension);
          }}
        />
      </NtModal>

      <NtModal ref={confirmRef}>
        <NtModalConfirmAlert
          title="Remove extension"
          message="Are you sure you want to remove this extension from the user?"
          onCancel={() => {
            confirmRef.current?.dismiss();
            setPendingDeleteId(null);
          }}
          onAccept={() => {
            confirmRef.current?.dismiss();
            handleDelete();
          }}
        />
      </NtModal>
    </NtPageSection>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    body: { padding: 8 },
    addBtn: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 4,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
    },
    addText: { fontSize: 11, color: t.brand2, fontWeight: "600" },
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 6,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
    },
    ico: {
      width: 28,
      height: 28,
      borderRadius: 6,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      alignItems: "center",
      justifyContent: "center",
    },
    icoText: { color: t.brand2, fontSize: 11, fontWeight: "700" },
    bodyCol: { flex: 1, minWidth: 0 },
    label: { color: t.text, fontSize: 13, fontWeight: "500" },
    meta: { color: t.textMute, fontSize: 11 },
    del: { padding: 4, borderRadius: 4 },
    empty: {
      color: t.textMute,
      fontSize: 12,
      paddingVertical: 16,
      paddingHorizontal: 6,
      fontStyle: "italic",
    },
  });

export default NtUserExtensions;
